import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import { get } from '../../utils/api';
import { formatCurrencyDisplay } from '../../pages/portal/portalDonateShared';
import OutOfTownLocationPanel from './OutOfTownLocationPanel';
import {
  mergedOutOfTownUi,
  normalizeOutOfTownDays,
  variationNeedsOutOfTownQuestion,
} from '../../utils/outOfTownSurcharge';

function isVariableProduct(p) {
  return String(p?.product_type || p?.type || '').toLowerCase() === 'variable';
}

const DEFAULT_DONATE_PRODUCT_IMAGE = '/assets/images/ICCT-Logo-trans.webp';

function productImageSrc(p) {
  const u = String(p?.image_url || p?.image || p?.images?.[0]?.src || '').trim();
  if (!u) return DEFAULT_DONATE_PRODUCT_IMAGE;
  if (/^https?:\/\//i.test(u)) return u;
  return u;
}

function onDonateProductImageError(e) {
  if (e?.currentTarget && e.currentTarget.src.indexOf(DEFAULT_DONATE_PRODUCT_IMAGE) === -1) {
    e.currentTarget.src = DEFAULT_DONATE_PRODUCT_IMAGE;
  }
}

/** Match icct-kiosk-app `ProductVariationDialog` label logic. */
function parseAttributes(raw) {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (!t) return null;
    try {
      return JSON.parse(t);
    } catch {
      return null;
    }
  }
  if (typeof raw === 'object') return raw;
  return null;
}

function prettify(val) {
  if (val == null) return 'Standard';
  const s = String(val).replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  return s.replace(/\b\w/g, (c) => c.toUpperCase());
}

function getVariationLabel(variation) {
  if (!variation) return 'Standard';
  const attrs = variation.attributes != null ? parseAttributes(variation.attributes) : null;
  if (Array.isArray(attrs) && attrs.length > 0) {
    const first = attrs[0];
    if (first && (first.option || first.name)) return prettify(first.option || first.name);
  }
  if (attrs && typeof attrs === 'object' && !Array.isArray(attrs)) {
    const values = Object.values(attrs).filter(Boolean);
    if (values.length > 0) {
      const v = values[0];
      if (v && typeof v === 'object' && (v.option || v.name)) return prettify(v.option || v.name);
      return prettify(v);
    }
  }
  const dn = String(variation.display_name || '').trim();
  if (dn) return dn;
  if (variation.name) return prettify(variation.name);
  return `Option #${variation.id}`;
}

/**
 * Donate catalog: env-driven quick select + search + list.
 * Search title / placeholder match icct-kiosk-app `ProductsPage.js` (shop search box).
 * @param {{ apiPrefix: string, cartPath: string, addItem: Function, itemCount: number, cart?: Array, removeItem?: Function, updateOutOfTownDays?: Function }} props
 */
function checkoutPathFromCart(cartPath) {
  const s = String(cartPath || '').replace(/\/$/, '');
  if (s.endsWith('/cart')) return `${s.slice(0, -'/cart'.length)}/checkout`;
  return `${s}/checkout`;
}

function productSlugFromUrl(productUrl) {
  const raw = String(productUrl || '').trim();
  if (!raw) return '';
  const noQuery = raw.split('?')[0];
  const noTrailingSlash = noQuery.replace(/\/+$/, '');
  const last = noTrailingSlash.split('/').filter(Boolean).pop() || '';
  return last.toLowerCase();
}

function slugifyProductName(name) {
  const s = String(name || '').trim();
  if (!s) return '';
  return s
    .replace(/\s*\([^)]*\)/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-+|-+$)/g, '');
}

function formatEventDateLabel(dateStr) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateStr || ''));
  if (!m) return String(dateStr || '');
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(d.getTime())) return String(dateStr);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function parseDateString(dateStr) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateStr || ''));
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatShortWeekday(dateStr) {
  const d = parseDateString(dateStr);
  if (!d) return '';
  return d.toLocaleDateString(undefined, { weekday: 'short' });
}

function formatDateRangeLabel(startStr, endStr) {
  const start = parseDateString(startStr);
  const end = parseDateString(endStr);
  if (!start || !end) return `${formatEventDateLabel(startStr)} – ${formatEventDateLabel(endStr)}`;
  const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
  if (sameMonth) {
    const month = start.toLocaleDateString(undefined, { month: 'short' });
    return `${month} ${start.getDate()} – ${end.getDate()}`;
  }
  return `${formatEventDateLabel(startStr)} – ${formatEventDateLabel(endStr)}`;
}

function parseQuickSelectProductGroups(raw) {
  return String(raw || '')
    .split('|')
    .map((group) => group
      .split(',')
      .map((id) => parseInt(String(id).trim(), 10))
      .filter((id) => Number.isFinite(id) && id > 0))
    .filter((group) => group.length > 0);
}

export default function DonateCatalogBrowse({
  apiPrefix,
  cartPath,
  addItem,
  itemCount,
  cart = [],
  removeItem = () => {},
  updateOutOfTownDays = () => {},
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [q, setQ] = React.useState('');
  const [debouncedQ, setDebouncedQ] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [loadingQuickSelect, setLoadingQuickSelect] = React.useState(true);
  const [error, setError] = React.useState('');
  const [products, setProducts] = React.useState([]);
  const [quickSelectGroups, setQuickSelectGroups] = React.useState([]);
  const [upcomingEvents, setUpcomingEvents] = React.useState([]);
  const [loadingUpcoming, setLoadingUpcoming] = React.useState(true);
  const [eventsExpanded, setEventsExpanded] = React.useState(false);
  const [allEvents, setAllEvents] = React.useState(null);
  const [loadingAllEvents, setLoadingAllEvents] = React.useState(false);
  const [snack, setSnack] = React.useState('');
  const [variationDialog, setVariationDialog] = React.useState({ open: false, product: null, loading: false, rows: [] });
  const [variationQuantities, setVariationQuantities] = React.useState({});
  const [lastAddedVarId, setLastAddedVarId] = React.useState(null);
  const [showInlineFA, setShowInlineFA] = React.useState(false);
  const [outOfTownUi, setOutOfTownUi] = React.useState(() => mergedOutOfTownUi());
  const [outOfTownByVariation, setOutOfTownByVariation] = React.useState({});
  /** At Home: show Shelby panel only after the user selects that row (or item is already in cart). */
  const [expandedOutOfTownVariationId, setExpandedOutOfTownVariationId] = React.useState(null);

  const dialogCategorySlugs = React.useMemo(
    () => (Array.isArray(variationDialog.product?.category_slugs) ? variationDialog.product.category_slugs : []),
    [variationDialog.product]
  );

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const path =
          apiPrefix.startsWith('/portal') ? '/portal/donate/fee-ui' : '/public/donate/fee-ui';
        const r = await get(path, {});
        if (!cancelled && r?.feeUi) setOutOfTownUi(mergedOutOfTownUi(r.outOfTown));
      } catch {
        /* defaults */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiPrefix]);

  React.useEffect(() => {
    if (!variationDialog.open || !variationDialog.product?.id) return;
    const pid = variationDialog.product.id;
    const next = {};
    (variationDialog.rows || []).forEach((v) => {
      const inCart = cart.find(
        (ci) => Number(ci.product_id) === Number(pid) && Number(ci.variation_id) === Number(v.id)
      );
      next[v.id] = normalizeOutOfTownDays(inCart?.out_of_town_days, outOfTownUi.maxDays);
    });
    setOutOfTownByVariation(next);
  }, [variationDialog.open, variationDialog.product, variationDialog.rows, cart, outOfTownUi.maxDays]);

  const closeVariationDialog = React.useCallback(() => {
    setVariationDialog({ open: false, product: null, loading: false, rows: [] });
    setVariationQuantities({});
    setLastAddedVarId(null);
    setShowInlineFA(false);
    setOutOfTownByVariation({});
    setExpandedOutOfTownVariationId(null);
  }, []);

  React.useEffect(() => {
    if (!variationDialog.open || variationDialog.loading) return;
    const rows = variationDialog.rows || [];
    if (!rows.length) return;
    const initial = {};
    rows.forEach((v) => {
      if (v?.id != null) initial[v.id] = 1;
    });
    setVariationQuantities(initial);
    setLastAddedVarId(null);
    setShowInlineFA(false);
  }, [variationDialog.open, variationDialog.loading, variationDialog.rows]);

  const isVariationInCart = React.useCallback(
    (productId, variationId) =>
      Array.isArray(cart) &&
      cart.some(
        (ci) =>
          Number(ci.product_id) === Number(productId) &&
          Number(ci.variation_id || 0) === Number(variationId)
      ),
    [cart]
  );

  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 220);
    return () => clearTimeout(t);
  }, [q]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingQuickSelect(true);
      try {
        const idGroups = parseQuickSelectProductGroups(process.env.REACT_APP_QUICK_ADD_PRODUCTS);
        if (idGroups.length === 0) {
          if (!cancelled) setQuickSelectGroups([]);
          return;
        }
        const uniqueIds = Array.from(new Set(idGroups.flat()));
        if (cancelled) return;
        const res = await get(`${apiPrefix}/products`, { product_ids: uniqueIds.join(',') });
        const productById = new Map();
        const rows = Array.isArray(res?.products) ? res.products : [];
        rows.forEach((row) => {
          if (row?.id) productById.set(String(row.id), row);
        });
        setQuickSelectGroups(
          idGroups
            .map((group) => group.map((id) => ({
              id,
              product: productById.get(String(id)) || null,
            })))
            .filter((group) => group.length > 0)
        );
      } catch {
        if (!cancelled) setQuickSelectGroups([]);
      } finally {
        if (!cancelled) setLoadingQuickSelect(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiPrefix]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingUpcoming(true);
      try {
        const envDaysRaw = parseInt(process.env.REACT_APP_UPCOMING_EVENTS_DAYS, 10);
        const params = Number.isFinite(envDaysRaw) && envDaysRaw > 0 ? { days: envDaysRaw } : {};
        const res = await get(`${apiPrefix}/upcoming-events`, params);
        if (cancelled) return;
        setUpcomingEvents(Array.isArray(res?.events) ? res.events : []);
      } catch {
        if (!cancelled) setUpcomingEvents([]);
      } finally {
        if (!cancelled) setLoadingUpcoming(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiPrefix]);

  React.useEffect(() => {
    if (!debouncedQ) {
      setProducts([]);
      setLoading(false);
      setError('');
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await get(`${apiPrefix}/products`, { q: debouncedQ, limit: 80 });
        if (cancelled) return;
        setProducts(Array.isArray(res?.products) ? res.products : []);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load products');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiPrefix, debouncedQ]);

  const listProducts = React.useMemo(() => products || [], [products]);

  const onAdd = async (product) => {
    setVariationDialog({ open: true, product, loading: true, rows: [] });
    try {
      const vRes = await get(`${apiPrefix}/variations`, { product_id: String(product.id), limit: 300 });
      const rows = Array.isArray(vRes?.variations) ? vRes.variations : [];
      if (rows.length === 0) {
        addItem({
          product_id: product.id,
          variation_id: 0,
          quantity: 1,
          product_name: product.name,
          variation_name: '',
          unit_price: Number(product?.price_num || 0),
        });
        setSnack(`Added ${product.name} to cart`);
        closeVariationDialog();
        return;
      }
      setVariationDialog({ open: true, product, loading: false, rows });
    } catch (e) {
      closeVariationDialog();
      setError(e?.message || 'Failed to load variations');
    }
  };

  const handleVariationRowClick = (variation, varLabel) => {
    const p = variationDialog.product;
    if (!p?.id || !variation?.id) return;
    const showOutOfTown = variationNeedsOutOfTownQuestion(dialogCategorySlugs, varLabel, p?.name);
    const inCart = isVariationInCart(p.id, variation.id);
    if (inCart) {
      handleVariationToggle(variation);
      setExpandedOutOfTownVariationId(null);
      return;
    }
    if (showOutOfTown) {
      setExpandedOutOfTownVariationId(variation.id);
      return;
    }
    setExpandedOutOfTownVariationId(null);
    handleVariationToggle(variation);
  };

  /** Kiosk-style: tap row or Add toggles; qty +/- before add (icct-kiosk-app `ProductVariationDialog`). */
  const handleVariationToggle = (variation) => {
    const p = variationDialog.product;
    if (!p?.id || !variation?.id) return;
    const inCart = isVariationInCart(p.id, variation.id);
    if (inCart) {
      removeItem(p.id, variation.id);
      setShowInlineFA(false);
      setLastAddedVarId(null);
      return;
    }
    const qty = Math.max(1, parseInt(variationQuantities[variation.id], 10) || 1);
    const label = getVariationLabel(variation);
    const payload = {
      product_id: p.id,
      variation_id: variation.id,
      quantity: qty,
      product_name: p.name,
      variation_name: label,
      unit_price: Number(variation.price_num || 0),
    };
    if (variationNeedsOutOfTownQuestion(dialogCategorySlugs, label, p.name)) {
      const days = normalizeOutOfTownDays(outOfTownByVariation[variation.id], outOfTownUi.maxDays);
      if (days > 0) payload.out_of_town_days = days;
    }
    addItem(payload);
    setSnack(`Added ${p.name} — ${label} to cart`);
    setLastAddedVarId(variation.id);
    setShowInlineFA(true);
  };

  const handleDialogOutOfTownChange = (variation, days) => {
    const d = normalizeOutOfTownDays(days, outOfTownUi.maxDays);
    setOutOfTownByVariation((prev) => ({ ...prev, [variation.id]: d }));
    const p = variationDialog.product;
    if (p?.id && isVariationInCart(p.id, variation.id)) {
      updateOutOfTownDays(p.id, variation.id, d);
    }
  };

  const handleToggleAllEvents = async () => {
    if (eventsExpanded) {
      setEventsExpanded(false);
      return;
    }
    if (allEvents != null) {
      setEventsExpanded(true);
      return;
    }
    setLoadingAllEvents(true);
    try {
      const res = await get(`${apiPrefix}/upcoming-events`, { all: 1 });
      setAllEvents(Array.isArray(res?.events) ? res.events : []);
      setEventsExpanded(true);
    } catch {
      setAllEvents([]);
      setEventsExpanded(true);
    } finally {
      setLoadingAllEvents(false);
    }
  };

  const renderUpcomingEvents = () => {
    if (loadingUpcoming) {
      return (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mt: debouncedQ ? 3 : 1.5,
            mb: 0,
            bgcolor: '#fff3e0',
            border: '1px solid',
            borderColor: '#ffcc80',
            borderRadius: 2,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <CircularProgress size={22} />
            <Typography variant="body2" color="text.secondary">Loading upcoming events…</Typography>
          </Stack>
        </Paper>
      );
    }
    if (!upcomingEvents.length) return null;
    const expandedSource = Array.isArray(allEvents) && allEvents.length ? allEvents : upcomingEvents;
    const visibleEvents = eventsExpanded ? expandedSource : upcomingEvents.slice(0, 3);
    const showToggle = upcomingEvents.length > 0;
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mt: debouncedQ ? 3 : 3,
          mb: 0,
          bgcolor: '#fff3e0',
          border: '1px solid #ffcc80',
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1 }}>
          <Box sx={{ width: { xs: 0, sm: 160 }, flexShrink: 0 }} />
          <Typography variant="h6" sx={{ fontWeight: 800, flex: 1, textAlign: 'center' }}>
            Upcoming Events
          </Typography>
          <Box sx={{ width: { xs: 'auto', sm: 160 }, display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
            {showToggle ? (
              <Button
                variant="text"
                size="small"
                onClick={handleToggleAllEvents}
                disabled={loadingAllEvents}
                startIcon={loadingAllEvents ? <CircularProgress size={14} /> : null}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  color: '#bf360c',
                  bgcolor: '#ffcc80',
                  border: '1px solid #ffb74d',
                  px: 1.5,
                  '&:hover': { bgcolor: '#ffb74d', borderColor: '#ffa726' },
                }}
              >
                {eventsExpanded
                  ? 'Show less'
                  : loadingAllEvents
                    ? 'Loading…'
                    : Array.isArray(allEvents)
                      ? `View all ${allEvents.length} events`
                      : 'View all events'}
              </Button>
            ) : null}
          </Box>
        </Box>
        <Box
          sx={{
            maxHeight: eventsExpanded ? 440 : 'none',
            overflowY: eventsExpanded ? 'scroll' : 'visible',
            pr: eventsExpanded ? 1 : 0,
            // Keep scrollbar visible across browsers so users can see there's more.
            '&::-webkit-scrollbar': { width: 10 },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#ffb74d',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#fff3e0',
              borderRadius: 4,
            },
            scrollbarWidth: 'thin',
            scrollbarColor: '#ffb74d #fff3e0',
          }}
        >
        <Stack spacing={1.5}>
          {visibleEvents.map((evt) => {
            const slug = productSlugFromUrl(evt.product_url) || slugifyProductName(evt.product_name);
            const showSponsor = !!evt.product_id && !!slug;
            return (
              <Paper
                key={evt.event_key}
                elevation={1}
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  borderRadius: 1.5,
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 1.25, sm: 2 },
                  alignItems: { xs: 'stretch', sm: 'flex-start' },
                  bgcolor: 'background.paper',
                }}
              >
                <Box
                  sx={{
                    width: { xs: 72, sm: 80 },
                    height: { xs: 72, sm: 80 },
                    flexShrink: 0,
                    bgcolor: '#f5f5f5',
                    borderRadius: 1,
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid',
                    borderColor: '#ffd9a8',
                  }}
                >
                  <Box
                    component="img"
                    src={evt.product_image_url || '/assets/images/ICCT-Logo-trans.webp'}
                    alt=""
                    onError={(e) => {
                      if (e.currentTarget.src.indexOf('/assets/images/ICCT-Logo-trans.webp') === -1) {
                        e.currentTarget.src = '/assets/images/ICCT-Logo-trans.webp';
                      }
                    }}
                    sx={{ width: '100%', height: '100%', objectFit: 'contain', p: 0.5 }}
                  />
                </Box>
                <Box
                  sx={{
                    width: { xs: '100%', sm: 132 },
                    height: { xs: 72, sm: 80 },
                    textAlign: { xs: 'left', sm: 'center' },
                    px: 1,
                    bgcolor: '#ffe0b2',
                    borderRadius: 1,
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                  }}
                >
                  {evt.is_multi_day ? (
                    <>
                      <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: '#bf360c' }}>
                        {formatShortWeekday(evt.start_date)} – {formatShortWeekday(evt.end_date)}
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#bf360c' }}>
                        {formatDateRangeLabel(evt.start_date, evt.end_date)}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: '#bf360c' }}>
                        {evt.event_day || formatShortWeekday(evt.event_date)}
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#bf360c' }}>
                        {formatEventDateLabel(evt.event_date)}
                      </Typography>
                    </>
                  )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                    {evt.title}
                  </Typography>
                  {evt.event_time ? (
                    <Typography variant="body2" color="text.secondary">
                      {evt.event_time}
                    </Typography>
                  ) : null}
                  {Array.isArray(evt.sessions) && evt.sessions.length > 0 ? (
                    <Box component="ul" sx={{ m: 0, mt: 0.75, pl: 2.25 }}>
                      {evt.sessions.map((s) => {
                        const dateLabel = s.event_date
                          ? `${formatShortWeekday(s.event_date)} ${formatEventDateLabel(s.event_date)}`
                          : '';
                        return (
                          <Typography
                            key={s.event_key}
                            component="li"
                            variant="body2"
                            color="text.secondary"
                            sx={{ lineHeight: 1.5 }}
                          >
                            {dateLabel ? <strong>{dateLabel}</strong> : null}
                            {dateLabel && s.event_time ? ', ' : null}
                            {s.event_time ? <strong>{s.event_time}</strong> : null}
                            {(dateLabel || s.event_time) ? ' — ' : null}
                            {s.title}
                          </Typography>
                        );
                      })}
                    </Box>
                  ) : null}
                </Box>
                {showSponsor ? (
                  <Button
                    component={NavLink}
                    to={`/product/${slug}`}
                    variant="contained"
                    color="primary"
                    sx={{
                      alignSelf: { xs: 'flex-start', sm: 'center' },
                      ml: { sm: 'auto' },
                      textTransform: 'none',
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    Become a Sponsor
                  </Button>
                ) : null}
              </Paper>
            );
          })}
        </Stack>
        </Box>
      </Paper>
    );
  };

  const renderQuickSelect = () => {
    if (loadingQuickSelect) {
      return (
        <Paper
          elevation={0}
          sx={{
          p: 2,
          mt: debouncedQ ? 3 : 1.5,
          mb: 0,
            bgcolor: '#f3e5f5',
            border: '1px solid',
            borderColor: '#e1bee7',
            borderRadius: 2,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <CircularProgress size={22} />
            <Typography variant="body2" color="text.secondary">Loading quick select…</Typography>
          </Stack>
        </Paper>
      );
    }
    if (!quickSelectGroups.length) return null;

    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mt: debouncedQ ? 3 : 3,
          mb: 0,
          bgcolor: '#f3e5f5',
          border: '1px solid #e1bee7',
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <Typography id="quick-select-label" variant="h6" gutterBottom sx={{ fontWeight: 800, textAlign: 'center', width: '100%' }}>
          Quick Select
        </Typography>
        <Stack spacing={2}>
          {quickSelectGroups.map((group, groupIdx) => (
            <Box
              key={`quick-select-group-${groupIdx}`}
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'minmax(0, 1fr)',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  md: 'repeat(4, minmax(0, 1fr))',
                },
                gap: 2.5,
                mb: groupIdx === quickSelectGroups.length - 1 ? 0 : 2,
                width: '100%',
                maxWidth: 900,
              }}
            >
              {group.map((slot) => {
                const p = slot.product;
                if (!p) {
                  return (
                    <Paper
                      key={`quick-select-missing-${slot.id}`}
                      elevation={1}
                      sx={{
                        p: 2,
                        minWidth: 0,
                        minHeight: 212,
                        borderRadius: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        opacity: 0.75,
                      }}
                    >
                      <Box
                        sx={{
                          width: 100,
                          height: 100,
                          bgcolor: '#f5f5f5',
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 1.25,
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">No image</Typography>
                      </Box>
                      <Box sx={{ minWidth: 0, width: '100%', flex: 1 }}>
                        <Typography variant="subtitle2" title={`Product #${slot.id}`} sx={{ width: '100%', fontWeight: 700, mb: 1 }}>
                          Product #{slot.id}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          Unavailable
                        </Typography>
                      </Box>
                      <Button variant="contained" color="primary" size="medium" fullWidth disabled>
                        Add to Cart
                      </Button>
                    </Paper>
                  );
                }
                const img = productImageSrc(p);
                const variable = isVariableProduct(p);
                return (
                  <Paper
                    key={`quick-select-${p.id}`}
                    elevation={1}
                    sx={{
                      p: 2,
                      minWidth: 180,
                      maxWidth: 200,
                      minHeight: 212,
                      borderRadius: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        width: 100,
                        height: 100,
                        bgcolor: '#f5f5f5',
                        borderRadius: 1,
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 1.25,
                      }}
                    >
                      <Box
                        component="img"
                        src={img}
                        alt=""
                        onError={onDonateProductImageError}
                        sx={{ width: '100%', height: '100%', objectFit: 'contain', p: 0.75 }}
                      />
                    </Box>
                    <Box sx={{ minWidth: 0, width: '100%', flex: 1 }}>
                      <Typography variant="subtitle2" noWrap title={p.name} sx={{ width: '100%', fontWeight: 700, mb: 1 }}>
                        {p.name}
                      </Typography>
                      {!variable && p.price_num != null ? (
                        <Typography variant="body2" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
                          {formatCurrencyDisplay(p.price_num)}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Options vary</Typography>
                      )}
                    </Box>
                    <Button variant="contained" color="primary" size="medium" fullWidth onClick={() => onAdd(p)}>
                      Add to Cart
                    </Button>
                  </Paper>
                );
              })}
            </Box>
          ))}
        </Stack>
      </Paper>
    );
  };

  return (
    <Stack spacing={2}>
      {itemCount > 0 ? (
        <Button
          component={NavLink}
          to={cartPath}
          variant="contained"
          color="secondary"
          startIcon={<ShoppingBagOutlinedIcon />}
          sx={{
            position: { xs: 'fixed', md: 'fixed' },
            right: { xs: 16, md: 24 },
            top: { xs: 'auto', md: '50%' },
            bottom: { xs: 18, md: 'auto' },
            transform: { xs: 'none', md: 'translateY(-50%)' },
            zIndex: 1200,
            borderRadius: 999,
            px: 2,
            py: 1.15,
            fontWeight: 800,
            boxShadow: '0 14px 32px rgba(190, 96, 0, 0.28)',
            bgcolor: '#ff6d00',
            color: '#fff',
            '&:hover': { bgcolor: '#e86400' },
          }}
        >
          Cart ({itemCount})
        </Button>
      ) : null}
      <Card variant="outlined">
        <CardContent>
          {!debouncedQ ? (
            <Box sx={{ mb: 2.5, width: '100%' }}>
              <Typography
                variant="subtitle1"
                component="h2"
                sx={{ fontWeight: 800, color: 'primary.dark', mb: 1, lineHeight: 1.35, width: '100%' }}
              >
                Welcome to India Cultural Center and Temple!
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, width: '100%' }}>
                ICCT gratefully welcomes your support. Your donations help sustain daily operations and
                services—you may give through Annual Poshaka, events and arjita sevas, special deity
                offerings, featured projects such as Kotilinga Kshetram, or a general donation for ongoing
                temple needs. All contributions are tax-deductible and help preserve Sanatana Dharma.
              </Typography>
            </Box>
          ) : null}

          <Typography variant="h6" gutterBottom>
            Search donations, events & services
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
            <TextField
              fullWidth
              size="medium"
              placeholder="Search donations, events and services..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
                sx: { bgcolor: 'background.paper' },
              }}
            />
          </Stack>

          {debouncedQ ? (
            <Box sx={{ mt: 2.5 }}>
              {loading ? (
                <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  {error ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {error}
                    </Alert>
                  ) : null}
                  {!error ? (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(3, minmax(0, 1fr))' },
                        gap: 2,
                        width: '100%',
                        maxWidth: '100%',
                        alignItems: 'start',
                      }}
                    >
                      {listProducts.map((p) => {
                        const img = productImageSrc(p);
                        const variable = isVariableProduct(p);
                        return (
                          <Box key={p.id} sx={{ display: 'flex', minWidth: 0, width: '100%' }}>
                            <Card
                              variant="outlined"
                              sx={(theme) => ({
                                width: '100%',
                                // Chandan / sandalwood-paste inspired warm cream (light), muted umber (dark)
                                bgcolor: theme.palette.mode === 'dark' ? '#3a342c' : '#faf5ed',
                                borderColor: theme.palette.mode === 'dark' ? '#5c5245' : '#decfbb',
                                boxShadow: theme.palette.mode === 'dark' ? 'none' : '0 1px 2px rgba(101, 76, 48, 0.06)',
                              })}
                            >
                              <CardContent
                                sx={{
                                  pt: 1.5,
                                  pb: 1.5,
                                  px: 1.5,
                                  '&:last-child': { pb: 1.5 },
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    gap: 1.5,
                                    alignItems: 'flex-start',
                                  }}
                                >
                                  <Box
                                    sx={(theme) => ({
                                      width: 96,
                                      height: 96,
                                      flexShrink: 0,
                                      bgcolor: theme.palette.mode === 'dark' ? '#2f2924' : '#f2e8dc',
                                      borderRadius: 1,
                                      overflow: 'hidden',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      border: '1px solid',
                                      borderColor: theme.palette.mode === 'dark' ? '#4a4238' : '#e5d8c8',
                                    })}
                                  >
                                    <Box
                                      component="img"
                                      src={img}
                                      alt=""
                                      onError={onDonateProductImageError}
                                      sx={{ width: '100%', height: '100%', objectFit: 'contain', p: 0.5 }}
                                    />
                                  </Box>
                                  <Box
                                    sx={{
                                      minWidth: 0,
                                      flex: 1,
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: 1,
                                    }}
                                  >
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        fontWeight: 700,
                                        lineHeight: 1.3,
                                        minWidth: 0,
                                        overflowWrap: 'anywhere',
                                      }}
                                    >
                                      {p.name}
                                    </Typography>
                                    {!variable && p.price_num != null ? (
                                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        {formatCurrencyDisplay(p.price_num)}
                                      </Typography>
                                    ) : null}
                                    <Button variant="outlined" size="small" onClick={() => onAdd(p)} fullWidth>
                                      {variable ? 'Choose options' : 'Add to cart'}
                                    </Button>
                                  </Box>
                                </Box>
                              </CardContent>
                            </Card>
                          </Box>
                        );
                      })}
                    </Box>
                  ) : null}
                  {!loading && !error && listProducts.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      No products found matching your search
                    </Typography>
                  ) : null}
                </>
              )}
            </Box>
          ) : null}

          {renderUpcomingEvents()}

          {renderQuickSelect()}
        </CardContent>
      </Card>

      {snack ? (
        <Alert
          severity="success"
          onClose={() => setSnack('')}
          action={(
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ alignItems: 'center' }}>
              <Button
                component={NavLink}
                to={checkoutPathFromCart(cartPath)}
                variant="outlined"
                size="small"
                color="inherit"
                sx={{ fontWeight: 700, borderColor: 'currentColor' }}
                onClick={() => setSnack('')}
              >
                Checkout
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="inherit"
                onClick={() => setSnack('')}
                sx={{ fontWeight: 700, borderWidth: 2, borderColor: 'currentColor' }}
              >
                Continue shopping
              </Button>
            </Stack>
          )}
        >
          {snack}
        </Alert>
      ) : null}

      <Dialog
        open={variationDialog.open}
        onClose={closeVariationDialog}
        fullWidth
        fullScreen={fullScreen}
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: fullScreen ? 0 : 3,
            maxHeight: fullScreen ? '100%' : '90vh',
            m: fullScreen ? 0 : 1.5,
            width: '100%',
            maxWidth: fullScreen ? '100%' : '840px',
            boxShadow: '0 6px 24px rgba(0,0,0,0.15)',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
            {variationDialog.product ? (
              <Box
                component="img"
                src={productImageSrc(variationDialog.product)}
                alt=""
                onError={onDonateProductImageError}
                sx={{ width: 48, height: 48, objectFit: 'contain', borderRadius: 1, bgcolor: '#f5f5f5', flexShrink: 0 }}
              />
            ) : null}
            <Typography variant="h6" component="div" sx={{ fontWeight: 700, lineHeight: 1.25 }} noWrap title={variationDialog.product?.name}>
              {variationDialog.product?.name || 'Choose option'}
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={closeVariationDialog}
            size="small"
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              bgcolor: '#ff4d4f',
              color: 'white',
              flexShrink: 0,
              '&:hover': { bgcolor: '#e53935' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0, bgcolor: 'background.default' }}>
          {variationDialog.loading ? (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Box>
              <Box sx={{ p: 2.5, pb: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Select an option and quantity
                </Typography>
              </Box>
              <Box sx={{ px: 2, pb: 2 }}>
                {variationDialog.rows.map((v) => {
                  const pid = variationDialog.product?.id;
                  const inCart = pid != null && isVariationInCart(pid, v.id);
                  const price = Number(v.price_num || 0);
                  const isCheap = price <= 10;
                  const varLabel = getVariationLabel(v);
                  const showOutOfTown = variationNeedsOutOfTownQuestion(
                    dialogCategorySlugs,
                    varLabel,
                    variationDialog.product?.name
                  );
                  const showOutOfTownPanel =
                    showOutOfTown && (inCart || Number(expandedOutOfTownVariationId) === Number(v.id));
                  return (
                    <Box
                      key={v.id}
                      sx={{
                        p: 1.5,
                        mb: 1,
                        border: inCart || showOutOfTownPanel ? '2px solid' : '1px solid',
                        borderColor: inCart || showOutOfTownPanel ? 'warning.main' : 'divider',
                        borderRadius: 1.5,
                        bgcolor: inCart || showOutOfTownPanel ? '#fff7ed' : 'background.paper',
                      }}
                    >
                      <Box
                        role="button"
                        tabIndex={0}
                        onClick={() => handleVariationRowClick(v, varLabel)}
                        onKeyDown={(ev) => {
                          if (ev.key === 'Enter' || ev.key === ' ') {
                            ev.preventDefault();
                            handleVariationRowClick(v, varLabel);
                          }
                        }}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          flexWrap: 'wrap',
                          cursor: 'pointer',
                        }}
                      >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {varLabel}
                        </Typography>
                      </Box>
                      <Typography variant="body1" fontWeight="bold" sx={{ minWidth: 88, textAlign: 'right' }}>
                        {formatCurrencyDisplay(v.price_num)}
                      </Typography>
                      {isCheap ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                          <IconButton
                            size="small"
                            onClick={() =>
                              setVariationQuantities((prev) => ({
                                ...prev,
                                [v.id]: Math.max(1, (parseInt(prev[v.id], 10) || 1) - 1),
                              }))
                            }
                            disabled={(variationQuantities[v.id] || 1) <= 1}
                          >
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                          <TextField
                            type="number"
                            size="small"
                            value={variationQuantities[v.id] || 1}
                            onChange={(e) =>
                              setVariationQuantities((prev) => ({
                                ...prev,
                                [v.id]: Math.max(1, parseInt(e.target.value, 10) || 1),
                              }))
                            }
                            inputProps={{ min: 1, style: { textAlign: 'center', width: 52 } }}
                          />
                          <IconButton
                            size="small"
                            onClick={() =>
                              setVariationQuantities((prev) => ({
                                ...prev,
                                [v.id]: (parseInt(prev[v.id], 10) || 1) + 1,
                              }))
                            }
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : null}
                      {inCart ? (
                        <IconButton
                          aria-label="Remove from cart"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleVariationToggle(v);
                          }}
                          sx={{ ml: 0.5 }}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      ) : (
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleVariationToggle(v);
                          }}
                          sx={{ ml: 0.5, textTransform: 'none', fontWeight: 700 }}
                        >
                          Add to cart
                        </Button>
                      )}
                      </Box>
                      {showOutOfTownPanel ? (
                        <Box sx={{ mt: 1.25 }} onClick={(e) => e.stopPropagation()}>
                          <OutOfTownLocationPanel
                            value={outOfTownByVariation[v.id] ?? 0}
                            onChange={(days) => handleDialogOutOfTownChange(v, days)}
                            dayRate={outOfTownUi.dayRate}
                            maxDays={outOfTownUi.maxDays}
                          />
                        </Box>
                      ) : null}
                      {showInlineFA && lastAddedVarId === v.id ? (
                        <Box
                          sx={{
                            flexBasis: '100%',
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            mt: 1.5,
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1}
                            sx={{
                              alignItems: 'center',
                              bgcolor: '#fff7ed',
                              border: '1px solid #fed7aa',
                              p: 1.25,
                              borderRadius: 2,
                              boxShadow: 2,
                            }}
                          >
                            <Button
                              variant="contained"
                              color="warning"
                              size="medium"
                              component={NavLink}
                              to={checkoutPathFromCart(cartPath)}
                              onClick={() => {
                                setShowInlineFA(false);
                                closeVariationDialog();
                              }}
                              sx={{ textTransform: 'none', fontWeight: 700 }}
                            >
                              Checkout
                            </Button>
                            <Button
                              variant="outlined"
                              color="warning"
                              size="medium"
                              onClick={() => {
                                setShowInlineFA(false);
                                closeVariationDialog();
                              }}
                              sx={{
                                textTransform: 'none',
                                fontWeight: 700,
                                borderWidth: 2,
                                borderColor: 'warning.dark',
                              }}
                            >
                              Continue shopping
                            </Button>
                          </Stack>
                        </Box>
                      ) : null}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', justifyContent: 'flex-start' }}>
          <Button
            onClick={closeVariationDialog}
            variant="contained"
            color="error"
            startIcon={<CloseIcon />}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 999 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
